import { useState, type CSSProperties, type ReactNode } from 'react';
import { Head } from 'vite-react-ssg';
import { SITE_URL, getStrings, localePath, type Lang } from '../../lib/i18n';
import { STORE_IOS, STORE_ANDROID } from '../../lib/constants';
import { hubStrings, embedSnippet, toolPath } from '../../lib/tools';
import { StoreBadges } from '../StoreBadges';

/** Copies text to the clipboard (with a textarea fallback) and flashes a "copied" label. */
export function CopyButton({
  getText,
  label,
  copiedLabel,
  className = 'btn-3d btn-3d--ghost btn-3d--sm px-4 py-2',
}: {
  getText: () => string;
  label: string;
  copiedLabel: string;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    const text = getText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* ignore */
      }
      ta.remove();
    }
    setDone(true);
    window.setTimeout(() => setDone(false), 2000);
  };
  return (
    <button type="button" onClick={copy} className={className} aria-live="polite">
      {done ? copiedLabel : label}
    </button>
  );
}

export function ToolHeroText({ eyebrow, h1, intro }: { eyebrow: string; h1: string; intro: string }) {
  return (
    <div className="mb-8 max-w-3xl">
      <span className="eyebrow-pill mb-4">{eyebrow}</span>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-ink leading-tight mt-3 mb-4">{h1}</h1>
      <p className="text-muted text-base sm:text-lg leading-relaxed">{intro}</p>
    </div>
  );
}

export function Breadcrumbs({ lang, current }: { lang: Lang; current?: string }) {
  const h = hubStrings(lang);
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-faint mb-6 print:hidden">
      <ol className="flex flex-wrap items-center gap-1.5">
        <li>
          <a href={localePath(lang, '/')} className="hover:text-ink">{h.home}</a>
        </li>
        <li aria-hidden="true">/</li>
        <li>
          {current ? (
            <a href={localePath(lang, '/tools')} className="hover:text-ink">{h.navLabel}</a>
          ) : (
            <span aria-current="page" className="text-muted">{h.navLabel}</span>
          )}
        </li>
        {current && (
          <>
            <li aria-hidden="true">/</li>
            <li aria-current="page" className="text-muted">{current}</li>
          </>
        )}
      </ol>
    </nav>
  );
}

export function ToolFaq({ heading, items }: { heading: string; items: { q: string; a: string }[] }) {
  return (
    <section className="mb-14" aria-labelledby="faq-heading">
      <h2 id="faq-heading" className="text-2xl md:text-3xl font-extrabold text-ink mb-5">{heading}</h2>
      <div className="space-y-3">
        {items.map((f, i) => (
          <details key={i} className="card-chunky p-5 group" open={i === 0}>
            <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-heading font-black text-ink text-lg">
              <h3 className="text-base sm:text-lg">{f.q}</h3>
              <span aria-hidden="true" className="text-primary text-xl leading-none transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="text-muted mt-3 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function ToolCta({ lang, heading, body }: { lang: Lang; heading: string; body: string }) {
  const s = getStrings(lang).store;
  return (
    <section
      className="mb-14 card-glow px-6 py-10 md:px-10 md:py-12 text-center print:hidden"
      style={{ '--glow': 'var(--brand-2)' } as CSSProperties}
    >
      <h2 className="font-heading text-2xl md:text-3xl font-black text-white mb-3">{heading}</h2>
      <p className="text-white/80 mb-7 max-w-lg mx-auto">{body}</p>
      <div className="flex flex-wrap justify-center gap-3">
        <StoreBadges iosHref={STORE_IOS} androidHref={STORE_ANDROID} s={s} lang={lang} />
      </div>
    </section>
  );
}

export function EmbedSection({
  lang,
  slug,
  title,
  credit,
  height,
}: {
  lang: Lang;
  slug: string;
  title: string;
  credit: string;
  height: number;
}) {
  const h = hubStrings(lang);
  const code = embedSnippet({ lang, slug, title, credit, height });
  return (
    <section className="mb-14 print:hidden" aria-labelledby="embed-heading">
      <h2 id="embed-heading" className="text-2xl md:text-3xl font-extrabold text-ink mb-3">{h.embedHeading}</h2>
      <p className="text-muted mb-4 max-w-2xl">{h.embedBody}</p>
      <label className="sr-only" htmlFor={`embed-${slug}`}>{h.embedHeading}</label>
      <textarea
        id={`embed-${slug}`}
        readOnly
        value={code}
        rows={4}
        className="field-chunky font-mono text-xs leading-relaxed mb-3"
        onFocus={(e) => e.currentTarget.select()}
      />
      <CopyButton getText={() => code} label={h.embedCopy} copiedLabel={h.embedCopied} />
    </section>
  );
}

/** Minimal chrome for /tools/<slug>/embed: noindex, canonical to the tool page, credit link. */
export function EmbedLayout({
  lang,
  slug,
  title,
  credit,
  children,
}: {
  lang: Lang;
  slug: string;
  title: string;
  credit: string;
  children: ReactNode;
}) {
  const page = toolPath(lang, slug);
  return (
    <div className="min-h-screen bg-dark px-3 py-4 sm:px-5">
      <Head>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}${page}`} />
      </Head>
      {children}
      <p className="mt-6 text-center text-sm text-faint print:hidden">
        <a href={page} target="_blank" rel="noopener" className="font-bold text-[var(--brand-ink)] hover:underline">
          {credit}
        </a>
      </p>
    </div>
  );
}
